/**
 * User context for managing authentication state across the app.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from './AuthContext';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { account, isAuthenticated, getAccessToken } = useAuth();

  useEffect(() => {
    // Fetch current user when auth state changes
    const fetchUser = async () => {
      if (!isAuthenticated || !account) {
        setLoading(false);
        return;
      }

      try {
        // First, get the access token and store it for API calls
        try {
          const token = await getAccessToken();
          if (token) {
            localStorage.setItem('msal.token', token);
          }
        } catch (tokenError) {
          console.error('Failed to get access token:', tokenError);
        }

        // Then fetch the user info from our backend
        try {
          const userData = await authAPI.getCurrentUser();
          setUser(userData);
        } catch (userError) {
          console.error('Failed to fetch user data from backend:', userError);

          // If backend user fetch fails, create minimal user from Entra ID account
          const minimalUser = {
            UserID: account.username,
            UserName: account.name,
            Email: account.username,
            Role: 'user', // Default role, will be overridden by backend if it's available
            // Extract other details from account if available
            AzureOID: account.localAccountId || account.homeAccountId,
          };

          setUser(minimalUser);
        }
      } catch (err) {
        console.error('Failed in user initialization:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [isAuthenticated, account, getAccessToken]);

  const value = {
    user,
    loading,
    error,
    isAdmin: user?.IsAdmin === true || user?.Role === 'admin',
    isManager: user?.IsManager === true || user?.Role === 'manager' || user?.Role === 'admin',
    isPlanner: user?.Role === 'planner',
    isDeveloperMonitor: user?.IsDeveloperMonitor === true,
    // Add Microsoft account details if needed
    msalAccount: account,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
